import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { 
  addCertificateToFirestore, 
  getAllCertificates, 
  updateCertificate, 
  deleteCertificate 
} from '../services/certificateAdminService';
import { getStandardsByCategory } from '../utils/certificateStandards';
import './CertificateAdmin.css';

const CertificateAdmin = () => {
  const [activeTab, setActiveTab] = useState('add'); // 'add' or 'manage'
  
  // Form state
  const [formData, setFormData] = useState({
    certificateNumber: '',
    companyName: '',
    companyAddress: '',
    standards: [],
    auditDate: '',
    issueDate: '',
    expiryDate: '',
    scope: ''
  });

  // Manage certificates state
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showStandardsDropdown, setShowStandardsDropdown] = useState(false);
  const [showEditStandardsDropdown, setShowEditStandardsDropdown] = useState(false);
  const standardsDropdownRef = useRef(null);
  const editStandardsDropdownRef = useRef(null);
  const modalRef = useRef(null);

  const standardsCategories = getStandardsByCategory();
  const navigate = useNavigate();

  // Auth durumunu kontrol et
  useEffect(() => {
    if (auth) {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          console.log('Admin authenticated as:', user.email);
        } else {
          console.warn('Admin not authenticated');
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // Sertifikaları yükle
  useEffect(() => {
    if (activeTab === 'manage') {
      loadCertificates();
    }
  }, [activeTab]);

  // Modal dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsModalOpen(false);
        setIsEditMode(false);
        setSelectedCertificate(null);
        setEditFormData(null);
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (standardsDropdownRef.current && !standardsDropdownRef.current.contains(event.target)) {
        setShowStandardsDropdown(false);
      }
      if (editStandardsDropdownRef.current && !editStandardsDropdownRef.current.contains(event.target)) {
        setShowEditStandardsDropdown(false);
      }
    };

    if (showStandardsDropdown || showEditStandardsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStandardsDropdown, showEditStandardsDropdown]);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const data = await getAllCertificates();
      setCertificates(data);
    } catch (err) {
      console.error('Error loading certificates:', err);
      setError('Failed to load certificates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStandardToggle = (standard) => {
    setFormData(prev => {
      const standards = [...prev.standards];
      const index = standards.indexOf(standard);
      
      if (index > -1) {
        standards.splice(index, 1);
      } else {
        standards.push(standard);
      }
      
      return {
        ...prev,
        standards
      };
    });
  };

  const handleEditStandardToggle = (standard) => {
    setEditFormData(prev => {
      const standards = [...prev.standards];
      const index = standards.indexOf(standard);
      
      if (index > -1) {
        standards.splice(index, 1);
      } else {
        standards.push(standard);
      }
      
      return {
        ...prev,
        standards
      };
    });
  };

  const handleStandardSelectAll = (categoryStandards) => {
    setFormData(prev => {
      const newStandards = [...prev.standards];
      categoryStandards.forEach(standard => {
        if (!newStandards.includes(standard)) {
          newStandards.push(standard);
        }
      });
      return {
        ...prev,
        standards: newStandards
      };
    });
  };

  const handleEditStandardSelectAll = (categoryStandards) => {
    setEditFormData(prev => {
      const newStandards = [...prev.standards];
      categoryStandards.forEach(standard => {
        if (!newStandards.includes(standard)) {
          newStandards.push(standard);
        }
      });
      return {
        ...prev,
        standards: newStandards
      };
    });
  };

  const handleStandardDeselectAll = (categoryStandards) => {
    setFormData(prev => ({
      ...prev,
      standards: prev.standards.filter(s => !categoryStandards.includes(s))
    }));
  };

  const handleEditStandardDeselectAll = (categoryStandards) => {
    setEditFormData(prev => ({
      ...prev,
      standards: prev.standards.filter(s => !categoryStandards.includes(s))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validation
      if (!formData.certificateNumber.trim()) {
        throw new Error('Certificate Number is required');
      }
      if (!formData.companyName.trim()) {
        throw new Error('Company Name is required');
      }
      if (!formData.issueDate || !formData.expiryDate) {
        throw new Error('Issue Date and Expiry Date are required');
      }

      await addCertificateToFirestore(formData);
      setSuccess(true);
      
      // Reset form
      setFormData({
        certificateNumber: '',
        companyName: '',
        companyAddress: '',
        standards: [],
        auditDate: '',
        issueDate: '',
        expiryDate: '',
        scope: ''
      });

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error submitting certificate:', err);
      setError(err.message || 'Failed to add certificate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateClick = (certificate) => {
    setSelectedCertificate(certificate);
    setIsEditMode(false);
    
    // Timestamp'leri string'e çevir
    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      if (timestamp.toDate) {
        return timestamp.toDate().toISOString().split('T')[0];
      }
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toISOString().split('T')[0];
      }
      return '';
    };

    setEditFormData({
      certificateNumber: certificate.certificateNumber || '',
      companyName: certificate.companyName || '',
      companyAddress: certificate.companyAddress || '',
      standards: certificate.standards || [],
      auditDate: formatDate(certificate.auditDate),
      issueDate: formatDate(certificate.issueDate),
      expiryDate: formatDate(certificate.expiryDate),
      scope: certificate.scope || ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!editFormData.certificateNumber.trim()) {
        throw new Error('Certificate Number is required');
      }
      if (!editFormData.companyName.trim()) {
        throw new Error('Company Name is required');
      }
      if (!editFormData.issueDate || !editFormData.expiryDate) {
        throw new Error('Issue Date and Expiry Date are required');
      }

      await updateCertificate(selectedCertificate.id, editFormData);
      setSuccess(true);
      setIsEditMode(false);
      await loadCertificates();
      
      setTimeout(() => {
        setSuccess(false);
        setIsModalOpen(false);
        setSelectedCertificate(null);
        setEditFormData(null);
      }, 2000);
    } catch (err) {
      console.error('Error updating certificate:', err);
      setError(err.message || 'Failed to update certificate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this certificate? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await deleteCertificate(selectedCertificate.id);
      setSuccess(true);
      await loadCertificates();
      
      setTimeout(() => {
        setSuccess(false);
        setIsModalOpen(false);
        setSelectedCertificate(null);
        setEditFormData(null);
      }, 2000);
    } catch (err) {
      console.error('Error deleting certificate:', err);
      setError(err.message || 'Failed to delete certificate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setSelectedCertificate(null);
    setEditFormData(null);
    setError(null);
    setSuccess(false);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return 'N/A';
  };

  const filteredCertificates = certificates.filter(cert => {
    const searchLower = searchTerm.toLowerCase();
    return (
      cert.certificateNumber?.toLowerCase().includes(searchLower) ||
      cert.companyName?.toLowerCase().includes(searchLower) ||
      cert.standards?.some(s => s.toLowerCase().includes(searchLower))
    );
  });

  const renderAddCertificateTab = () => (
    <form className="certificate-form" onSubmit={handleSubmit}>
      <div className="form-section">
        <h2 className="form-section-title">Certificate Information</h2>
        
        <div className="form-group">
          <label htmlFor="certificateNumber" className="form-label">
            Certificate Number <span className="required">*</span>
          </label>
          <input
            type="text"
            id="certificateNumber"
            name="certificateNumber"
            className="form-input"
            value={formData.certificateNumber}
            onChange={handleChange}
            placeholder="e.g., DOC-NQC-1269"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="companyName" className="form-label">
            Company Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="companyName"
            name="companyName"
            className="form-input"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="Full company name"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="companyAddress" className="form-label">
            Company Address
          </label>
          <textarea
            id="companyAddress"
            name="companyAddress"
            className="form-textarea"
            value={formData.companyAddress}
            onChange={handleChange}
            placeholder="Complete company address"
            rows="3"
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-section">
        <h2 className="form-section-title">Standards & Dates</h2>
        
        <div className="form-group">
          <label htmlFor="standards" className="form-label">
            Standards
          </label>
          <div className="standards-selector" ref={standardsDropdownRef}>
            <div 
              className="standards-input"
              onClick={() => !loading && setShowStandardsDropdown(!showStandardsDropdown)}
            >
              <div className="standards-selected">
                {formData.standards.length === 0 ? (
                  <span className="standards-placeholder">Select standards...</span>
                ) : (
                  <span className="standards-count">{formData.standards.length} standard(s) selected</span>
                )}
              </div>
              <span className="standards-arrow">{showStandardsDropdown ? '▲' : '▼'}</span>
            </div>
            
            {showStandardsDropdown && (
              <div className="standards-dropdown">
                {standardsCategories.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="standards-category">
                    <div className="standards-category-header">
                      <h4 className="standards-category-title">{category.category}</h4>
                      <div className="standards-category-actions">
                        <button
                          type="button"
                          className="standards-action-btn"
                          onClick={() => handleStandardSelectAll(category.standards)}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          className="standards-action-btn"
                          onClick={() => handleStandardDeselectAll(category.standards)}
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    <div className="standards-list">
                      {category.standards.map((standard, index) => (
                        <label key={index} className="standards-option">
                          <input
                            type="checkbox"
                            checked={formData.standards.includes(standard)}
                            onChange={() => handleStandardToggle(standard)}
                            disabled={loading}
                          />
                          <span className="standards-option-label">{standard}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {formData.standards.length > 0 && (
            <div className="standards-selected-list">
              <strong>Selected:</strong>
              <div className="standards-tags">
                {formData.standards.map((standard, index) => (
                  <span key={index} className="standards-tag">
                    {standard}
                    <button
                      type="button"
                      className="standards-tag-remove"
                      onClick={() => handleStandardToggle(standard)}
                      disabled={loading}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="auditDate" className="form-label">
              Audit Date
            </label>
            <input
              type="date"
              id="auditDate"
              name="auditDate"
              className="form-input"
              value={formData.auditDate}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="issueDate" className="form-label">
              Issue Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="issueDate"
              name="issueDate"
              className="form-input"
              value={formData.issueDate}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="expiryDate" className="form-label">
              Expiry Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="expiryDate"
              name="expiryDate"
              className="form-input"
              value={formData.expiryDate}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2 className="form-section-title">Scope</h2>
        
        <div className="form-group">
          <label htmlFor="scope" className="form-label">
            Scope of Certification
          </label>
          <textarea
            id="scope"
            name="scope"
            className="form-textarea form-textarea-large"
            value={formData.scope}
            onChange={handleChange}
            placeholder="Enter the detailed scope of certification"
            rows="6"
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-actions">
        <button
          type="submit"
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Adding Certificate...' : 'Add Certificate'}
        </button>
        <button
          type="button"
          className="reset-button"
          onClick={() => {
            setFormData({
              certificateNumber: '',
              companyName: '',
              companyAddress: '',
              standards: [],
              auditDate: '',
              issueDate: '',
              expiryDate: '',
              scope: ''
            });
            setError(null);
            setSuccess(false);
            setShowStandardsDropdown(false);
          }}
          disabled={loading}
        >
          Reset Form
        </button>
      </div>
    </form>
  );

  const renderManageCertificatesTab = () => (
    <div className="manage-certificates">
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by certificate number, company name, or standards..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {loading && certificates.length === 0 ? (
        <div className="loading-message">Loading certificates...</div>
      ) : filteredCertificates.length === 0 ? (
        <div className="no-certificates">
          {searchTerm ? 'No certificates found matching your search.' : 'No certificates found.'}
        </div>
      ) : (
        <div className="certificates-list">
          {filteredCertificates.map((cert) => (
            <div
              key={cert.id}
              className="certificate-item"
              onClick={() => handleCertificateClick(cert)}
            >
              <div className="certificate-item-header">
                <h3 className="certificate-item-number">{cert.certificateNumber}</h3>
                <span className="certificate-item-date">
                  Issued: {formatTimestamp(cert.issueDate)}
                </span>
              </div>
              <div className="certificate-item-company">{cert.companyName}</div>
              <div className="certificate-item-standards">
                {cert.standards && cert.standards.length > 0 ? (
                  <span className="standards-badge">
                    {cert.standards.length} standard(s)
                  </span>
                ) : (
                  <span className="no-standards">No standards</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderModal = () => {
    if (!isModalOpen || !selectedCertificate) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content" ref={modalRef}>
          <div className="modal-header">
            <h2 className="modal-title">
              {isEditMode ? 'Edit Certificate' : 'Certificate Details'}
            </h2>
            <button
              type="button"
              className="modal-close"
              onClick={handleCloseModal}
            >
              ×
            </button>
          </div>

          {success && (
            <div className="admin-alert admin-alert-success">
              <strong>✓ Success!</strong> Certificate has been updated successfully.
            </div>
          )}

          {error && (
            <div className="admin-alert admin-alert-error">
              <strong>✗ Error:</strong> {error}
            </div>
          )}

          <div className="modal-body">
            {isEditMode ? (
              <div className="edit-form">
                <div className="form-group">
                  <label className="form-label">
                    Certificate Number <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="certificateNumber"
                    className="form-input"
                    value={editFormData?.certificateNumber || ''}
                    onChange={handleEditChange}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    className="form-input"
                    value={editFormData?.companyName || ''}
                    onChange={handleEditChange}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Company Address</label>
                  <textarea
                    name="companyAddress"
                    className="form-textarea"
                    value={editFormData?.companyAddress || ''}
                    onChange={handleEditChange}
                    rows="3"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Standards</label>
                  <div className="standards-selector" ref={editStandardsDropdownRef}>
                    <div 
                      className="standards-input"
                      onClick={() => !loading && setShowEditStandardsDropdown(!showEditStandardsDropdown)}
                    >
                      <div className="standards-selected">
                        {editFormData?.standards?.length === 0 ? (
                          <span className="standards-placeholder">Select standards...</span>
                        ) : (
                          <span className="standards-count">
                            {editFormData?.standards?.length || 0} standard(s) selected
                          </span>
                        )}
                      </div>
                      <span className="standards-arrow">
                        {showEditStandardsDropdown ? '▲' : '▼'}
                      </span>
                    </div>
                    
                    {showEditStandardsDropdown && (
                      <div className="standards-dropdown">
                        {standardsCategories.map((category, categoryIndex) => (
                          <div key={categoryIndex} className="standards-category">
                            <div className="standards-category-header">
                              <h4 className="standards-category-title">{category.category}</h4>
                              <div className="standards-category-actions">
                                <button
                                  type="button"
                                  className="standards-action-btn"
                                  onClick={() => handleEditStandardSelectAll(category.standards)}
                                >
                                  Select All
                                </button>
                                <button
                                  type="button"
                                  className="standards-action-btn"
                                  onClick={() => handleEditStandardDeselectAll(category.standards)}
                                >
                                  Deselect All
                                </button>
                              </div>
                            </div>
                            <div className="standards-list">
                              {category.standards.map((standard, index) => (
                                <label key={index} className="standards-option">
                                  <input
                                    type="checkbox"
                                    checked={editFormData?.standards?.includes(standard) || false}
                                    onChange={() => handleEditStandardToggle(standard)}
                                    disabled={loading}
                                  />
                                  <span className="standards-option-label">{standard}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {editFormData?.standards?.length > 0 && (
                    <div className="standards-selected-list">
                      <strong>Selected:</strong>
                      <div className="standards-tags">
                        {editFormData.standards.map((standard, index) => (
                          <span key={index} className="standards-tag">
                            {standard}
                            <button
                              type="button"
                              className="standards-tag-remove"
                              onClick={() => handleEditStandardToggle(standard)}
                              disabled={loading}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Audit Date</label>
                    <input
                      type="date"
                      name="auditDate"
                      className="form-input"
                      value={editFormData?.auditDate || ''}
                      onChange={handleEditChange}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Issue Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="issueDate"
                      className="form-input"
                      value={editFormData?.issueDate || ''}
                      onChange={handleEditChange}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Expiry Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="expiryDate"
                      className="form-input"
                      value={editFormData?.expiryDate || ''}
                      onChange={handleEditChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Scope</label>
                  <textarea
                    name="scope"
                    className="form-textarea form-textarea-large"
                    value={editFormData?.scope || ''}
                    onChange={handleEditChange}
                    rows="6"
                    disabled={loading}
                  />
                </div>
              </div>
            ) : (
              <div className="certificate-details">
                <div className="detail-row">
                  <strong>Certificate Number:</strong>
                  <span>{selectedCertificate.certificateNumber}</span>
                </div>
                <div className="detail-row">
                  <strong>Company Name:</strong>
                  <span>{selectedCertificate.companyName}</span>
                </div>
                <div className="detail-row">
                  <strong>Company Address:</strong>
                  <span>{selectedCertificate.companyAddress || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <strong>Standards:</strong>
                  <div className="standards-list-view">
                    {selectedCertificate.standards && selectedCertificate.standards.length > 0 ? (
                      selectedCertificate.standards.map((standard, index) => (
                        <span key={index} className="standard-badge">{standard}</span>
                      ))
                    ) : (
                      <span>No standards</span>
                    )}
                  </div>
                </div>
                <div className="detail-row">
                  <strong>Audit Date:</strong>
                  <span>{formatTimestamp(selectedCertificate.auditDate)}</span>
                </div>
                <div className="detail-row">
                  <strong>Issue Date:</strong>
                  <span>{formatTimestamp(selectedCertificate.issueDate)}</span>
                </div>
                <div className="detail-row">
                  <strong>Expiry Date:</strong>
                  <span>{formatTimestamp(selectedCertificate.expiryDate)}</span>
                </div>
                <div className="detail-row">
                  <strong>Scope:</strong>
                  <span>{selectedCertificate.scope || 'N/A'}</span>
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            {isEditMode ? (
              <>
                <button
                  type="button"
                  className="submit-button"
                  onClick={handleUpdate}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="reset-button"
                  onClick={() => {
                    setIsEditMode(false);
                    setEditFormData({
                      certificateNumber: selectedCertificate.certificateNumber || '',
                      companyName: selectedCertificate.companyName || '',
                      companyAddress: selectedCertificate.companyAddress || '',
                      standards: selectedCertificate.standards || [],
                      auditDate: editFormData?.auditDate || '',
                      issueDate: editFormData?.issueDate || '',
                      expiryDate: editFormData?.expiryDate || '',
                      scope: selectedCertificate.scope || ''
                    });
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="submit-button"
                  onClick={handleEdit}
                  disabled={loading}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="certificate-admin-container">
      <div className="certificate-admin-content">
        <div className="admin-header">
          <div className="admin-header-content">
            <div>
              <h1 className="admin-title">Certificate Management</h1>
              <p className="admin-subtitle">Nova Quality Certification Agency - Certificate Management</p>
            </div>
            <button
              type="button"
              className="logout-button"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>

        <div className="tabs">
          <button
            type="button"
            className={`tab-button ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => setActiveTab('add')}
          >
            Add New Certificate
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            Manage Certificates
          </button>
        </div>

        {success && activeTab === 'add' && (
          <div className="admin-alert admin-alert-success">
            <strong>✓ Success!</strong> Certificate has been added successfully to the database.
          </div>
        )}

        {error && (
          <div className="admin-alert admin-alert-error">
            <strong>✗ Error:</strong> {error}
          </div>
        )}

        {activeTab === 'add' && renderAddCertificateTab()}
        {activeTab === 'manage' && renderManageCertificatesTab()}

        {renderModal()}

        <div className="admin-footer">
          <p>
            <a href="/certificate-query" className="admin-link">View Certificate Query</a> | 
            <a href="/" className="admin-link"> Back to Home</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CertificateAdmin;
